import { Typography } from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import { FontSize, FontWeight } from '../../../../../styles/commonStyles'
import { Button } from '../../../../components/buttons'
import { useCallback, useState } from 'react'
import { intl } from '../../../../../i18n'
import { WbsItemSearchSearchConditionV2 } from '../../hooks/useSearchConditionV2'
import WbsSearchSearchConditionDialogV2 from './WbsSearchSearchConditionDialogV2'
import { WbsItemSearchSearchItemLabelsV2 } from './WbsItemSearchSearchHeader/WbsItemSearchSearchItemLabelsV2'
import { PermanentUiState } from '../../../../../services/model/uiState'
import { SearchConditionAndColumnStateV2 } from '../../../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import SaveSearchConditionListDialogV2 from './SaveSearchConditionListDialogV2'
import { WBS_ITEM_SEARCH_COUNT_LIMIT } from '../../wbsItemSearch'

type Props = {
  hit: number
  count: number
  total: number
  searchCondition: WbsItemSearchSearchConditionV2
  onChange: (patch: Partial<WbsItemSearchSearchConditionV2>) => void
  search: () => void
  resetCondition: () => void
  functionUuid: string
  applyStoredState: (storedState: SearchConditionAndColumnStateV2) => void
  onCloseSearchItemLabel: () => void
  isLoading: boolean
}

const WbsItemSearchSearchConditionHeaderV2 = ({
  hit,
  count,
  total,
  searchCondition,
  search,
  onChange,
  resetCondition,
  functionUuid,
  applyStoredState,
  onCloseSearchItemLabel,
  isLoading,
}: Props) => {
  const [openSearchConditionPanel, setOpenSearchConditionPanel] =
    useState<boolean>(false)
  const [openSavedSearchConditionPanel, setOpenSavedSearchConditionPanel] =
    useState<boolean>(false)

  const onSearch = useCallback(() => {
    search()
    setOpenSearchConditionPanel(false)
  }, [search])

  const handleSearchConditionPanelClose = () => {
    setOpenSearchConditionPanel(false)
    if (count <= WBS_ITEM_SEARCH_COUNT_LIMIT) {
      search()
    }
  }

  const handleSearchConditionPanelOpen = () => {
    setOpenSearchConditionPanel(true)
  }

  const handleSavedSearchConditionPanelClose = () => {
    setOpenSavedSearchConditionPanel(false)
  }

  const handleSavedSearchConditionPanelOpen = () => {
    setOpenSavedSearchConditionPanel(true)
  }

  const onSelectState = useCallback(
    (selected: PermanentUiState<SearchConditionAndColumnStateV2>) => {
      applyStoredState(selected.value)
    },
    [applyStoredState]
  )

  const [filteringInput, setFilteringInput] = useState<string>('')

  return (
    <div
      style={{
        display: 'flex',
        borderRadius: '4px',
        padding: '10px',
        backgroundColor: 'rgba(216, 221, 229, 0.3)',
        margin: '10px 20px 10px 20px',
        flexDirection: 'column',
        height: 'fit-content',
      }}
    >
      <HeaderRow>
        <Typography
          sx={{
            fontSize: FontSize.LARGE,
            fontWeight: 700,
            color: colorPalette.monotone[10],
            marginRight: '20px',
            marginLeft: '10px',
          }}
        >
          {intl.formatMessage({
            id: 'wbsItemSearchFilterPanel.filteringItems.label',
          })}
        </Typography>
        <ResultInfoContainer>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: FontWeight.NORMAL,
              marginTop: '1px',
              marginRight: '10px',
            }}
          >
            {intl.formatMessage({
              id: 'wbs.search.header.search.panel.result',
            })}
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[10],
              fontWeight: 700,
              fontSize: FontSize.LARGE,
            }}
          >
            {intl.formatMessage(
              { id: 'wbs.search.header.search.hits' },
              { hit }
            )}
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: 500,
              margin: '5px 5px 0 5px',
            }}
          >
            /
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: 500,
              margin: '5px 0 0 0',
            }}
          >
            {intl.formatMessage(
              { id: 'wbs.search.header.search.total' },
              { total }
            )}
          </Typography>
        </ResultInfoContainer>
        <div>
          <Button
            onClick={handleSearchConditionPanelOpen}
            colorPattern="skyBlue"
            disabled={isLoading}
            sx={{ border: 'none', margin: '0 5px', cursor: 'pointer' }}
          >
            <Typography sx={{ fontSize: '12px' }}>
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.add.condition',
              })}
            </Typography>
          </Button>
          <WbsSearchSearchConditionDialogV2
            count={count}
            open={openSearchConditionPanel}
            onClose={handleSearchConditionPanelClose}
            searchCondition={searchCondition}
            onEnter={search}
            onChangeCondition={onChange}
            resetCondition={resetCondition}
            search={onSearch}
            filteringInput={filteringInput}
            setFilteringInput={setFilteringInput}
            onSelectState={onSelectState}
            functionUuid={functionUuid}
          />
        </div>
        <Button
          onClick={handleSavedSearchConditionPanelOpen}
          colorPattern="monotone"
          variant="outlined"
          sx={{ margin: '0 5px', cursor: 'pointer' }}
        >
          <Typography sx={{ fontSize: '12px' }}>
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.saved.condition',
            })}
          </Typography>
        </Button>
        <SaveSearchConditionListDialogV2
          open={openSavedSearchConditionPanel}
          handleSavedSearchConditionPanelClose={
            handleSavedSearchConditionPanelClose
          }
          filteringInput={filteringInput}
          setFilteringInput={setFilteringInput}
          onSelectState={onSelectState}
          functionUuid={functionUuid}
          searchCondition={searchCondition}
        />
      </HeaderRow>
      <WbsItemSearchSearchItemLabelsV2
        searchCondition={searchCondition}
        onChange={onChange}
        search={search}
        onClose={onCloseSearchItemLabel}
        isLoading={isLoading}
      />
    </div>
  )
}

const HeaderRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const ResultInfoContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: '20px',
  marginLeft: '10px',
})

export default WbsItemSearchSearchConditionHeaderV2
